import createDOMPurify, { type Config, type DOMPurify } from 'dompurify';
import { VIZ_LIMITS } from '../limits.ts';

/**
 * The host page's sanitizer for the SVG a viz Worker returns (ADR 0013). The markup was produced by
 * untrusted code, so it is treated as hostile: whatever it holds, what reaches the page is static
 * SVG that cannot run script, load anything, or link anywhere.
 *
 * - DOMPurify's SVG profile (and SVG filters), nothing of HTML or MathML.
 * - Never: script, foreignObject, iframe/embed/object, <a>, <image> and <feImage> (external or data
 *   images), <style> (CSS can load URLs), animation elements (animate/set can rewrite href), and
 *   <use> (left out of DOMPurify's SVG profile: it can pull in other documents).
 * - No event attributes (on*), no data attributes.
 * - href / xlink:href only `#fragment`; any attribute value with `url(` only `url(#fragment)`.
 * - The style attribute keeps only an allow-list of presentation properties with plain values.
 * - Exactly one <svg> root, at most VIZ_LIMITS.svgElements elements.
 */
export interface SanitizedSvg {
  fragment: DocumentFragment;
  /** The sanitized markup (for tests and a copy action). */
  markup: string;
  elements: number;
  /** What was taken out: tag and attribute names, once each. */
  removed: string[];
}

export class UnsafeSvgError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsafeSvgError';
  }
}

const FORBID_TAGS = [
  'script',
  'foreignObject',
  'foreignobject',
  'iframe',
  'frame',
  'embed',
  'object',
  'a',
  'image',
  'feImage',
  'feimage',
  'style',
  'link',
  'meta',
  'base',
  'animate',
  'animateMotion',
  'animatemotion',
  'animateTransform',
  'animatetransform',
  'animateColor',
  'animatecolor',
  'set',
  'discard',
  'handler',
  'listener',
  'audio',
  'video',
  'canvas',
  'font-face',
  'font-face-uri',
  'cursor',
  'color-profile',
];
const FORBID_ATTR = [
  'attributeName',
  'attributename',
  'begin',
  'end',
  'from',
  'to',
  'values',
  'by',
  'formaction',
  'action',
  'src',
  'srcset',
  'ping',
  'download',
];

const FRAGMENT = /^#[A-Za-z_][\w.:-]{0,200}$/;
const URL_FN = /url\s*\(/gi;
const FRAGMENT_URL = /url\(\s*(['"]?)#[A-Za-z_][\w.:-]{0,200}\1\s*\)/gi;

/** Style properties a chart needs; everything else in a style attribute is dropped. */
const STYLE_PROPS = new Set([
  'fill',
  'fill-opacity',
  'fill-rule',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'opacity',
  'color',
  'stop-color',
  'stop-opacity',
  'font',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'font-variant-numeric',
  'text-anchor',
  'dominant-baseline',
  'alignment-baseline',
  'baseline-shift',
  'letter-spacing',
  'word-spacing',
  'text-decoration',
  'white-space',
  'display',
  'visibility',
  'shape-rendering',
  'text-rendering',
  'paint-order',
  'vector-effect',
  'mix-blend-mode',
  'isolation',
  'pointer-events',
  'transform',
  'transform-origin',
  'transform-box',
  'clip-path',
  'clip-rule',
  'mask',
  'marker',
  'marker-start',
  'marker-mid',
  'marker-end',
  'filter',
]);
/** A plain CSS value: no escapes, no at-rules, no nested declarations, no strings with markup. */
const SAFE_STYLE_VALUE = /^[\w\s#%.,()+\-*/'"!]*$/;

/** Every url( in the value is url(#fragment). */
function urlsAreFragments(value: string): boolean {
  const all = value.match(URL_FN)?.length ?? 0;
  if (!all) return true;
  const ok = value.match(FRAGMENT_URL)?.length ?? 0;
  return ok === all;
}

/** The allowed part of a style attribute, or '' for none. */
export function cleanStyle(style: string): string {
  if (/[\\@<>{}]|\/\*|expression|image-set|-moz-binding|behavior|javascript:/i.test(style)) return '';
  const out: string[] = [];
  for (const decl of style.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const value = decl.slice(i + 1).trim();
    if (!STYLE_PROPS.has(prop) || !value || !SAFE_STYLE_VALUE.test(value) || !urlsAreFragments(value))
      continue;
    out.push(`${prop}: ${value}`);
  }
  return out.join('; ');
}

const CONFIG: Config = {
  USE_PROFILES: { svg: true, svgFilters: true },
  FORBID_TAGS,
  FORBID_ATTR,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // DOMPurify tests every attribute value against this: no value may start with a scheme
  // (javascript:, data:, http:, …). href / xlink:href are narrowed to #fragments by the hook.
  ALLOWED_URI_REGEXP: /^(?:[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  KEEP_CONTENT: false,
  WHOLE_DOCUMENT: false,
  RETURN_DOM_FRAGMENT: true,
};

const instances = new WeakMap<object, DOMPurify>();

function purifierFor(win: Window & typeof globalThis): DOMPurify {
  let p = instances.get(win);
  if (p) return p;
  p = createDOMPurify(win);
  if (!p.isSupported) throw new UnsafeSvgError('This browser cannot sanitize SVG safely.');
  p.addHook('uponSanitizeAttribute', (_node, data) => {
    const name = data.attrName.toLowerCase();
    const value = data.attrValue ?? '';
    if (name.startsWith('on')) {
      data.keepAttr = false;
      return;
    }
    if (name === 'href' || name === 'xlink:href') {
      if (!FRAGMENT.test(value.trim())) data.keepAttr = false;
      return;
    }
    if (name === 'style') {
      const clean = cleanStyle(value);
      if (clean) data.attrValue = clean;
      else data.keepAttr = false;
      return;
    }
    if (/url\s*\(/i.test(value) && !urlsAreFragments(value)) data.keepAttr = false;
    // Values that smuggle a scheme into an attribute a renderer might resolve.
    if (
      /^\s*(javascript|data|vbscript|blob|https?|file|ftp):/i.test(value) &&
      name !== 'xmlns' &&
      !name.startsWith('xmlns:')
    )
      data.keepAttr = false;
  });
  instances.set(win, p);
  return p;
}

/**
 * Sanitizes `svg` for `win`'s document. Throws UnsafeSvgError when nothing usable is left (no
 * single <svg> root, or too many elements).
 */
export function sanitizeVizSvg(svg: string, win: Window & typeof globalThis = window): SanitizedSvg {
  if (typeof svg !== 'string' || !svg.trim()) throw new UnsafeSvgError('The chart is empty.');
  if (svg.length > VIZ_LIMITS.svgChars)
    throw new UnsafeSvgError(`The chart's SVG is larger than ${VIZ_LIMITS.svgChars} characters.`);
  const purify = purifierFor(win);
  const fragment = purify.sanitize(svg, CONFIG) as unknown as DocumentFragment;
  const removed = [
    ...new Set(
      purify.removed.map((r) => {
        const x = r as { element?: Element; attribute?: Attr | null };
        if (x.attribute) return `@${x.attribute.name}`;
        return x.element?.nodeName?.toLowerCase() ?? '?';
      }),
    ),
  ];
  // Whitespace text around the root is dropped; anything else beside one <svg> is refused.
  for (const n of [...fragment.childNodes])
    if (n.nodeType === 3 && !n.textContent?.trim()) fragment.removeChild(n);
  const roots = [...fragment.childNodes];
  const root = roots[0] as Element | undefined;
  if (roots.length !== 1 || !root || root.nodeType !== 1 || root.nodeName.toLowerCase() !== 'svg')
    throw new UnsafeSvgError('The chart is not a single <svg> element.');
  const elements = root.querySelectorAll('*').length + 1;
  if (elements > VIZ_LIMITS.svgElements)
    throw new UnsafeSvgError(`The chart has ${elements} elements; the cap is ${VIZ_LIMITS.svgElements}.`);
  return { fragment, markup: root.outerHTML, elements, removed };
}
